import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { useOutsideClick, useIntl, useMobileViewport } from 'hooks';
import {
  Card,
  Stack,
  Space,
  Label,
  Avatar,
  Icon,
  Badge,
  Button,
  Divider,
  cr,
} from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import { CHAT_TYPE, WS_CLEAR_NOTIFY, WS_NEW_NOTIFY } from 'constants';
import { SET_ACTIVE_ACTOR } from '@control-front-end/common/constants/graphActors';
import history from '@control-front-end/app/src/store/history';
import useNotificationSync from '../../hooks/useNotificationSync';
import mes from './intl';
import './Notifications.scss';

function Notifications({ collapsed }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const wsNotify = useSelector((state) => state.wsNotify);
  const { plainMode } = useSelector((state) => state.settings);
  const { active: accId } = useSelector((state) => state.accounts);
  const [notify, setNotify] = useState(false);
  const popupRef = useRef();
  const notifyRef = useRef();

  const { syncClearNotification, syncClearAllNotifications } =
    useNotificationSync();

  const isMobile = useMobileViewport();
  const isEventsRoute = history.location.pathname.includes('/events/');

  const handleClose = () => {
    setNotify(false);
  };

  useOutsideClick({
    ref: popupRef,
    callback: (e) => {
      if (notifyRef.current?.contains(e.target)) return;
      handleClose();
    },
  });

  const markAsRead = () => {
    dispatch({ type: WS_CLEAR_NOTIFY });
    syncClearAllNotifications();
    handleClose();
  };

  const getTimeLabels = () => [
    t(mes.justNow),
    t(mes.secondsAgo),
    t(mes.minuteAgo),
    t(mes.minutesAgo),
    t(mes.hourAgo),
    t(mes.hoursAgo),
    t(mes.yesterday1),
  ];

  const handleClickNotify = ({ actorId, rid, url }) => {
    const newArr = wsNotify.filter((i) => i.actorId !== actorId);
    dispatch({ type: WS_NEW_NOTIFY, payload: newArr });
    syncClearNotification(actorId);
    handleClose();
    if (isEventsRoute && plainMode && rid) {
      dispatch({
        type: SET_ACTIVE_ACTOR,
        payload: { id: actorId, rid },
      });
    } else {
      history.push(url);
    }
  };

  const renderNotificationMessage = (item) => {
    const getNotificationDetails = () => {
      const defaultText = t(mes[`notify_${item.type}`]);
      if (item.type === 'comment') {
        return cr(
          [item.appId, t(mes.notify_script)],
          [
            item.chatType === CHAT_TYPE.group,
            <Label color={Label.COLOR.gray} value={`• ${item.title}`} />,
          ],
          [item.chatType !== CHAT_TYPE.p2p, defaultText],
          [true, null]
        );
      }
      return defaultText;
    };

    return (
      <>
        &nbsp;{getNotificationDetails(item)}&nbsp;
        {item.chatType ? null : <a href={item.url}>{item.title}</a>}
      </>
    );
  };

  const renderEmpty = () => (
    <Space size="xxlarge" top bottom fullWidth fullHeight>
      <Stack.V alignItems="center" justifyContent="center">
        <Label fontWeight="semibold" value={t(mes.noNotify)} />
        <Label value={t(mes.noNotifyInfo)} />
      </Stack.V>
    </Space>
  );

  const renderNotificationItem = (item) => (
    <Stack.H
      key={item.id}
      styleName="notify__item"
      onClick={() => handleClickNotify(item)}
      fullWidth
    >
      <Space size="small" fullWidth>
        <Stack.H size="small" alignItems="center" fullWidth>
          <Avatar styleName="notify__item__avatar" src={item.ownerAvatar} />
          <Stack.V size="xxsmall" fullWidth>
            <div styleName="notify__item__content__row">
              <Label fontWeight="semibold" value={item.ownerName} />
              {renderNotificationMessage(item)}
            </div>
            <div
              styleName="notify__item__content__row"
              dangerouslySetInnerHTML={{
                __html: AppUtils.makeDescription({
                  str: item.description,
                  accId,
                  chip: true,
                  noNewLines: true,
                }),
              }}
            />
            <Label
              fontSize="small"
              color={Label.COLOR.gray}
              value={AppUtils.relativeDate(item.createdAt, getTimeLabels())}
            />
          </Stack.V>
        </Stack.H>
      </Space>
    </Stack.H>
  );

  return (
    <div ref={notifyRef} styleName={cn('notify', { collapsed })}>
      <Icon
        styleName="notify__icon"
        type="notification"
        onClick={() => setNotify((prevNotify) => !prevNotify)}
      />
      {wsNotify.length ? <Badge styleName="notify__badge" /> : null}
      <div styleName="notify__items">
        {notify && (
          <Card
            forwardRef={popupRef}
            styleName="notify__popup"
            color={Card.COLOR.secondary}
            borderRadius={Card.BORDER_RADIUS.xlarge}
            boxShadow={Card.BOX_SHADOW.intense}
          >
            <Stack.H
              styleName="notify__popup__header"
              alignItems="center"
              justifyContent="spaceBetween"
              fullWidth
            >
              <Button
                styleName="notify__popup__btn"
                type="quinary"
                fontWeight="normal"
                label={t(mes.cancel)}
                onClick={handleClose}
              />
              <Label
                fontSize={isMobile ? 'small' : 'large'}
                fontWeight="semibold"
                value={t(mes.notifications)}
              />
              <Button
                styleName="notify__popup__btn"
                type="quinary"
                label={t(mes.markAsRead)}
                fontWeight="normal"
                onClick={markAsRead}
                disabled={!wsNotify.length}
              />
            </Stack.H>
            <Stack.V
              styleName="notify__list"
              size="none"
              fullHeight
              fullWidth
              divider={<Divider styleName="notify__list__divider" />}
            >
              {wsNotify.length
                ? wsNotify.map(renderNotificationItem)
                : renderEmpty()}
            </Stack.V>
          </Card>
        )}
      </div>
    </div>
  );
}

Notifications.propTypes = {
  collapsed: PropTypes.bool,
};

export default Notifications;
